import { type PropsWithChildren, type ReactNode, type MouseEvent, useEffect } from 'react';
import { cn, PaletteUtils } from '@shared/utils';
import { Button, Icon, Spinner, Text } from '../index';
import CloseIcon from '@assets/icons/close.svg';

export type ModalProps = PropsWithChildren & {
  className?: string;
  isOpen: boolean;
  onClose?: () => void;
  loading?: boolean;
  title?: ReactNode;
  width?: string;
  position?: 'top' | 'center';
  setAutoCloseSeconds?: number;
};

export const Modal = (props: ModalProps) => {
  useEffect(() => {
    if (!props.isOpen || !props.setAutoCloseSeconds) return;
    const timerId = setTimeout(() => props.onClose?.(), props.setAutoCloseSeconds * 1000);
    return () => clearTimeout(timerId);
  }, [props.isOpen, props.setAutoCloseSeconds, props.onClose]);

  const handleOverlayClick = (e: MouseEvent) => {
    if (e.currentTarget === e.target) props.onClose?.();
  };

  if (!props.isOpen) return null;
  return (
    <div className='fixed left-0 top-0 z-[99] flex h-dvh w-dvw bg-almost-dark-black' onMouseDown={handleOverlayClick}>
      <div
        className={cn(
          'flex max-h-[90vh] min-w-[332px] flex-col overflow-y-auto p-6',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-eerie-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl bg-ice dark:bg-soft-black',
          (!props.position || props.position === 'center') && 'm-auto',
          props.position === 'top' && 'absolute left-1/2 top-[5dvh] -translate-x-1/2',
          props.className,
        )}
        style={{ width: props.width }}
      >
        <div className='mb-4 flex justify-between'>
          <Text className='pr-8' size='2xl'>
            {props.title}
          </Text>
          {props.onClose && (
            <Button color='icon' onClick={props.onClose}>
              <Icon icon={<CloseIcon />} />
            </Button>
          )}
        </div>
        {props.loading ? <Spinner /> : props.children}
      </div>
    </div>
  );
};
